import type { StylesConfig } from "react-select";

import { ControlProps, CSSObjectWithLabel, GroupBase } from "react-select";
import colors from "tailwindcss/colors";

// Base styles shared by all react-select components
export const getBaseSelectStyles = <T, IsMulti extends boolean>(): StylesConfig<
  T,
  IsMulti,
  GroupBase<T>
> => ({
  control: (
    provided: CSSObjectWithLabel,
    state: ControlProps<T, IsMulti, GroupBase<T>>,
  ): CSSObjectWithLabel => ({
    ...provided,
    backgroundColor: colors.zinc[900],
    borderColor: state.isFocused ? colors.gray[500] : colors.gray[600],
    borderRadius: "0.375rem",
    borderWidth: "1px",
    padding: "2px",
  }),
  dropdownIndicator: (provided: CSSObjectWithLabel): CSSObjectWithLabel => ({
    ...provided,
    color: colors.gray[400],
  }),
  input: (provided: CSSObjectWithLabel): CSSObjectWithLabel => ({
    ...provided,
    caretColor: "white",
    color: "white",
  }),
  menu: (provided: CSSObjectWithLabel): CSSObjectWithLabel => ({
    ...provided,
    backgroundColor: colors.zinc[800],
    borderRadius: "0.375rem",
    padding: "6px",
  }),
  menuPortal: (base: CSSObjectWithLabel): CSSObjectWithLabel => ({
    ...base,
    zIndex: 9999,
  }),
  option: (
    provided: CSSObjectWithLabel,
    state: { isFocused: boolean; isSelected: boolean },
  ): CSSObjectWithLabel => ({
    ...provided,
    backgroundColor: state.isSelected
      ? colors.gray[600]
      : state.isFocused
        ? colors.gray[700]
        : "transparent",
    borderRadius: "0.375rem",
    color: state.isSelected ? "white" : colors.gray[300],
    cursor: "pointer",
    padding: "8px",
  }),
  placeholder: (provided: CSSObjectWithLabel): CSSObjectWithLabel => ({
    ...provided,
    color: colors.gray[400],
  }),
});

// Compact base styles for smaller popup components
export const getBaseSelectStylesCompact = <
  T,
  IsMulti extends boolean,
>(): StylesConfig<T, IsMulti, GroupBase<T>> => ({
  control: (
    provided: CSSObjectWithLabel,
    state: ControlProps<T, IsMulti, GroupBase<T>>,
  ): CSSObjectWithLabel => ({
    ...provided,
    backgroundColor: colors.zinc[900],
    borderColor: state.isFocused ? colors.gray[500] : colors.gray[600],
    borderRadius: "0.375rem",
    borderWidth: "1px",
    fontSize: "12px",
    minHeight: "28px",
    padding: "0 4px",
  }),
  dropdownIndicator: (provided: CSSObjectWithLabel): CSSObjectWithLabel => ({
    ...provided,
    color: colors.gray[400],
  }),
  input: (provided: CSSObjectWithLabel): CSSObjectWithLabel => ({
    ...provided,
    caretColor: "white",
    color: "white",
    fontSize: "12px",
    margin: 0,
    padding: 0,
  }),
  menu: (provided: CSSObjectWithLabel): CSSObjectWithLabel => ({
    ...provided,
    backgroundColor: colors.zinc[800],
    borderRadius: "0.375rem",
    marginTop: "4px",
    padding: "6px",
  }),
  menuList: (provided: CSSObjectWithLabel): CSSObjectWithLabel => ({
    ...provided,
    maxHeight: "200px",
    padding: "4px",
  }),
  menuPortal: (base: CSSObjectWithLabel): CSSObjectWithLabel => ({
    ...base,
    zIndex: 9999,
  }),
  option: (
    provided: CSSObjectWithLabel,
    state: { isFocused: boolean; isSelected: boolean },
  ): CSSObjectWithLabel => ({
    ...provided,
    backgroundColor: state.isSelected
      ? colors.gray[600]
      : state.isFocused
        ? colors.gray[700]
        : "transparent",
    borderRadius: "0.375rem",
    color: state.isSelected ? "white" : colors.gray[300],
    cursor: "pointer",
    fontSize: "12px",
    padding: "4px 8px",
  }),
  placeholder: (provided: CSSObjectWithLabel): CSSObjectWithLabel => ({
    ...provided,
    color: colors.gray[400],
    fontSize: "12px",
  }),
});

// Styles specific to single-select
export const singleSelectStyles = {
  singleValue: (provided: CSSObjectWithLabel): CSSObjectWithLabel => ({
    ...provided,
    color: "white",
  }),
};

// Styles specific to multi-select
export const multiSelectStyles = {
  multiValue: (provided: CSSObjectWithLabel): CSSObjectWithLabel => ({
    ...provided,
    backgroundColor: colors.gray[700],
    borderRadius: "0.25rem",
  }),
  multiValueLabel: (provided: CSSObjectWithLabel): CSSObjectWithLabel => ({
    ...provided,
    color: "white",
  }),
  multiValueRemove: (provided: CSSObjectWithLabel): CSSObjectWithLabel => ({
    ...provided,
    ":hover": {
      backgroundColor: colors.gray[600],
      color: "white",
    },
    color: colors.gray[400],
  }),
};
